import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';

import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer } from 'recharts';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { mkConfig, generateCsv, download } from 'export-to-csv';

const csvConfig = mkConfig({ fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtHours = (h) => (h != null ? `${Number(h).toFixed(2)}h` : '—');

const get30DaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

const getToday = () => new Date().toISOString().split('T')[0];

const getCF = (customFields = [], name) =>
  customFields.find((cf) => cf.name?.toLowerCase().includes(name.toLowerCase()))?.value || null;

const getFacturable = (customFields = []) => {
  const value = getCF(customFields, 'facturable');
  if (value == null || value === '') return null;
  if (String(value) === '1') return 'Oui';
  if (String(value) === '0') return 'Non';
  return String(value);
};

const getDeplacementLabel = (customFields = []) => {
  const value = getCF(customFields, 'deplacement');
  if (value == null || value === '') return null;
  return String(value) === '1' ? 'Oui' : 'Non';
};

const uniqueSortedValues = (values = []) =>
  Array.from(
    new Set(
      values
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

const CHART_COLORS = {
  deplacement: '#f59e0b',
  yes: '#f59e0b',
  no: '#e2e8f0',
};

const TRACKER_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#64748b'];

const PRESETS = {
  deplacement: { label: 'Déplacements/IK', icon: <DirectionsCarIcon fontSize="small" /> },
  commande: { label: 'Tracker Commande', icon: <ShoppingCartIcon fontSize="small" /> },
  helpdesk: { label: 'Tracker Helpdesk', icon: <SupportAgentIcon fontSize="small" /> },
};

const StatCard = ({ label, value, sub, icon, color = '#3b82f6', pct }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2,
      flex: 1,
      minWidth: 140,
      borderColor: `${color}30`,
      background: `linear-gradient(135deg, ${color}08 0%, transparent 60%)`,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ position: 'absolute', top: 10, right: 12, opacity: 0.15, fontSize: 36, color }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}
    >
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {pct !== undefined ? (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        {pct}% du total
      </Typography>
    ) : null}
    {sub ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
        {sub}
      </Typography>
    ) : null}
  </Paper>
);

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function buildFilterParams(columnFilters, globalFilter, preset) {
  const params = {};

  if (globalFilter) params.search = globalFilter;

  for (const f of columnFilters) {
    const val = f.value;
    if (val === undefined || val === null || val === '') continue;

    switch (f.id) {
      case 'user':
        params.user = val;
        break;
      case 'activity':
        params.activity = val;
        break;
      case 'project':
        params.projectName = val;
        break;
      case 'organization':
        params.orgName = val;
        break;
      case 'tracker':
        params.trackerName = val;
        break;
      case 'ticket':
        params.ticketSubject = val;
        break;
      case 'comments':
        params.comments = val;
        break;
      case 'facturable':
        params.facturable = val;
        break;
      case 'deplacement':
        params.deplacement = String(val).toLowerCase() === 'oui' ? 'oui' : String(val).toLowerCase() === 'non' ? 'non' : val;
        break;
      case 'hours':
        if (Array.isArray(val)) {
          if (val[0] != null && val[0] !== '') params.hoursMin = val[0];
          if (val[1] != null && val[1] !== '') params.hoursMax = val[1];
        }
        break;
      case 'spentOn':
        if (Array.isArray(val)) {
          if (val[0]) params.from = new Date(val[0]).toISOString().split('T')[0];
          if (val[1]) params.to = new Date(val[1]).toISOString().split('T')[0];
        }
        break;
      default:
        break;
    }
  }

  if (preset === 'deplacement') params.deplacement = 'oui';
  if (preset === 'commande') params.trackerName = 'Commande';
  if (preset === 'helpdesk') params.trackerName = 'Helpdesk';

  return params;
}

export default function EntryTimeERM({ orgId }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [totalHours, setTotalHours] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([{ id: 'spentOn', desc: true }]);
  const [preset, setPreset] = useState(null);
  const [grouping, setGrouping] = useState([]);
  const [viewMode, setViewMode] = useState('entries');
  const [showOverrunsOnly, setShowOverrunsOnly] = useState(false);
  const [filterOptionStore, setFilterOptionStore] = useState({
    organizations: [],
    projects: [],
    trackers: [],
    activities: [],
    users: [],
    facturable: [],
    deplacement: [],
  });

  const [dateFrom, setDateFrom] = useState(get30DaysAgo);
  const [dateTo, setDateTo] = useState(getToday);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handlePresetChange = useCallback((_, val) => {
    setPreset(val);
    setGrouping(val === 'deplacement' ? ['user'] : []);
  }, []);

  const getFilterValue = useCallback(
    (filterId) => columnFilters.find((filter) => filter.id === filterId)?.value ?? '',
    [columnFilters]
  );

  const setColumnFilterValue = useCallback((filterId, value) => {
    setColumnFilters((prev) => {
      const nextValue = value == null ? '' : String(value);
      const nextFilters = prev.filter((filter) => filter.id !== filterId);
      if (nextValue === '') return nextFilters;
      return [...nextFilters, { id: filterId, value: nextValue }];
    });
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const filterParams = buildFilterParams(columnFilters, globalFilter, preset);
      const sortParam = sorting[0]?.id || 'spentOn';
      const sortDirParam = sorting[0]?.desc ? 'desc' : 'asc';

      const params = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        sort: sortParam,
        sortDir: sortDirParam,
        includeSummary: true,
        groupBy: viewMode === 'ticket' ? 'ticket' : undefined,
        overrunOnly: showOverrunsOnly ? true : undefined,
        ...(orgId ? { orgId } : {}),
        ...(dateFrom ? { from: dateFrom } : {}),
        ...(dateTo ? { to: dateTo } : {}),
        ...filterParams,
      };

      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/easyredmine/time-entries`, { params });

      setEntries(Array.isArray(data?.data) ? data.data : []);
      setSummary(data?.summary || null);
      setTotal(data?.pagination?.total || 0);
      setTotalHours(data?.totalHours || 0);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erreur');
      setEntries([]);
      setSummary(null);
      setTotal(0);
      setTotalHours(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, columnFilters, globalFilter, sorting, preset, orgId, dateFrom, dateTo, viewMode, showOverrunsOnly]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [columnFilters, globalFilter, preset, dateFrom, dateTo, viewMode, showOverrunsOnly]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const buildCsvRow = (e) => ({
    date: fmtDate(e.spentOn),
    utilisateur: e.user || '',
    activite: e.activity || '',
    heures: e.hours ?? 0,
    projet: e.project?.name || e.projectName || '',
    organisation: e.organization?.name || '',
    ticket: e.ticket?.subject || e.ticketSubject || '',
    commentaire: e.comments || '',
    deplacement: getCF(e.customFields, 'deplacement') || '',
    facturable: getFacturable(e.customFields) || '',
    temps_estime: e.ticket?.estimated_hours ?? '',
  });

  const handleExportRows = (rows) => {
    download(csvConfig)(generateCsv(csvConfig)(rows.map((r) => buildCsvRow(r.original))));
    setAnchorEl(null);
  };

  const handleExportAll = () => {
    download(csvConfig)(generateCsv(csvConfig)(visibleEntries.map(buildCsvRow)));
    setAnchorEl(null);
  };

  const visibleEntries = entries;

  const currentFilterOptions = useMemo(
    () => ({
      organizations: uniqueSortedValues(visibleEntries.map((entry) => entry.organization?.name)),
      projects: uniqueSortedValues(visibleEntries.map((entry) => entry.project?.name || entry.projectName)),
      trackers: uniqueSortedValues(visibleEntries.map((entry) => entry.tracker || entry.ticket?.tracker)),
      activities: uniqueSortedValues(visibleEntries.map((entry) => entry.activity)),
      users: uniqueSortedValues(visibleEntries.map((entry) => entry.user)),
      facturable: uniqueSortedValues(visibleEntries.map((entry) => getFacturable(entry.customFields))),
      deplacement: uniqueSortedValues(visibleEntries.map((entry) => getDeplacementLabel(entry.customFields))),
    }),
    [visibleEntries]
  );

  useEffect(() => {
    setFilterOptionStore((prev) => ({
      organizations: uniqueSortedValues([...prev.organizations, ...currentFilterOptions.organizations]),
      projects: uniqueSortedValues([...prev.projects, ...currentFilterOptions.projects]),
      trackers: uniqueSortedValues([...prev.trackers, ...currentFilterOptions.trackers]),
      activities: uniqueSortedValues([...prev.activities, ...currentFilterOptions.activities]),
      users: uniqueSortedValues([...prev.users, ...currentFilterOptions.users]),
      facturable: uniqueSortedValues([...prev.facturable, ...currentFilterOptions.facturable]),
      deplacement: uniqueSortedValues([...prev.deplacement, ...currentFilterOptions.deplacement]),
    }));
  }, [currentFilterOptions]);

  const filterOptions = useMemo(
    () => ({
      organizations: uniqueSortedValues([...filterOptionStore.organizations, getFilterValue('organization')]),
      projects: uniqueSortedValues([...filterOptionStore.projects, getFilterValue('project')]),
      trackers: uniqueSortedValues([...filterOptionStore.trackers, getFilterValue('tracker')]),
      activities: uniqueSortedValues([...filterOptionStore.activities, getFilterValue('activity')]),
      users: uniqueSortedValues([...filterOptionStore.users, getFilterValue('user')]),
      facturable: uniqueSortedValues([...filterOptionStore.facturable, getFilterValue('facturable')]),
      deplacement: uniqueSortedValues([...filterOptionStore.deplacement, getFilterValue('deplacement')]),
    }),
    [filterOptionStore, getFilterValue]
  );

  const overrunCount = Number(summary?.overrunCount || 0);

  const columns = useMemo(
    () => [
      {
        id: 'timeentry',
        columns: [
          {
            accessorFn: (row) => (row.spentOn ? new Date(row.spentOn) : null),
            id: 'spentOn',
            header: 'Date',
            size: 100,
            filterVariant: 'date-range',
            sortingFn: 'datetime',
            Cell: ({ row }) => (
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtDate(row.original.spentOn)}
              </Typography>
            ),
          },
          {
            accessorFn: (row) => row.ticket?.ticketId || '',
            id: 'ticketId',
            header: 'N° Ticket',
            size: 100,
            filterVariant: 'text',
            Cell: ({ renderedCellValue, row }) => {
              if (!renderedCellValue) {
                return (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                );
              }

              return (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    variant="body2"
                    noWrap
                    component="a"
                    href={`https://toolbox.easyredmine.com/issues/${renderedCellValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    #{renderedCellValue}
                  </Typography>
                  {row.original.grouped ? (
                    <Chip size="small" variant="outlined" label={`${row.original.entryCount} lignes`} />
                  ) : null}
                </Stack>
              );
            },
          },
          { accessorKey: 'user', header: 'Utilisateur', size: 150, filterVariant: 'text' },
          {
            accessorKey: 'activity',
            header: 'Activité',
            size: 130,
            filterVariant: 'text',
            Cell: ({ renderedCellValue }) => <Chip label={renderedCellValue || '—'} size="small" variant="outlined" />,
          },

          {
            accessorFn: (row) => row.ticket?.estimated_hours ?? null,
            id: 'estimatedHours',
            header: 'Temps estimé',
            size: 110,
            enableColumnFilter: false,
            Cell: ({ renderedCellValue }) => (
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {fmtHours(renderedCellValue)}
              </Typography>
            ),
          },
          {
            accessorKey: 'hours',
            header: 'Temps passé',
            size: 90,
            filterVariant: 'range',
            Cell: ({ renderedCellValue, row }) => {
              const estimatedHours = Number(row.original.ticket?.estimated_hours || 0);
              const spentHours = Number(row.original.hours || 0);
              const isOverrun = estimatedHours > 0 && spentHours > estimatedHours;

              return (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 13 }} color="action" />
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {fmtHours(renderedCellValue)}
                  </Typography>
                  {isOverrun ? (
                    <Tooltip
                      placement="top"
                      title={`Temps cumulé ${fmtHours(spentHours)} supérieur au temps estimé ${fmtHours(estimatedHours)}`}
                    >
                      <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </Tooltip>
                  ) : null}
                </Stack>
              );
            },
          },
          {
            accessorFn: (row) => row.project?.name || row.projectName || '',
            id: 'project',
            header: 'Projet',
            size: 200,
            filterVariant: 'text',
            Cell: ({ renderedCellValue, row }) => (
              <Tooltip title={row.original.project?.parentProjectName || ''} placement="top">
                <Typography variant="body2" noWrap>
                  {renderedCellValue || '—'}
                </Typography>
              </Tooltip>
            ),
          },
          {
            accessorFn: (row) => row.organization?.name || '',
            id: 'organization',
            header: 'Organisation',
            size: 150,
            filterVariant: 'text',
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <Chip label={renderedCellValue} size="small" variant="outlined" />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ),
          },
          {
            accessorFn: (row) => row.tracker || '',
            id: 'tracker',
            header: 'Tracker',
            size: 110,
            enableColumnFilter: true,
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <Chip
                  label={renderedCellValue}
                  size="small"
                  // color={renderedCellValue === 'Oui' ? 'success' : renderedCellValue === 'Non' ? 'default' : 'warning'}
                  variant="outlined"
                />
              ) : (
                <Typography variant="caption">
                  —
                </Typography>
              ),
          },
          {
            accessorFn: (row) => getFacturable(row.customFields),
            id: 'facturable',
            header: 'Facturable',
            size: 110,
            enableColumnFilter: true,
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <Chip
                  label={renderedCellValue}
                  size="small"
                  // color={renderedCellValue === 'Oui' ? 'success' : renderedCellValue === 'Non' ? 'default' : 'warning'}
                  variant="outlined"
                />
              ) : (
                <Typography variant="caption">
                  —
                </Typography>
              ),
          },
          {
            accessorFn: (row) => {
              const cf = (row.customFields || []).find((c) => c.name?.toLowerCase().includes('placement'));
              return cf?.value === '1' ? 'Oui' : 'Non';
            },
            id: 'deplacement',
            header: 'Déplacement',
            size: 110,
            filterVariant: 'select',
            filterSelectOptions: ['Oui', 'Non'],
            enableColumnFilter: false,
            Cell: ({ renderedCellValue }) => {
              if (renderedCellValue !== 'Oui') {
                return (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                );
              }

              return <Chip icon={<DirectionsCarIcon />} label="Oui" size="small" />;
            },
          },
          {
            accessorFn: (row) => row.ticket?.subject || row.ticketSubject || '',
            id: 'ticket',
            header: 'Ticket',
            size: 240,
            filterVariant: 'text',
            Cell: ({ renderedCellValue, row }) => {
              if (!renderedCellValue) {
                return (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                );
              }

              const ticketId = row.original.ticket?.ticketId;

              return (
                <Tooltip title={row.original.ticket?.priority || ''} placement="top">
                  {ticketId ? (
                    <Typography
                      variant="body2"
                      noWrap
                      component="a"
                      href={`https://toolbox.easyredmine.com/issues/${ticketId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ maxWidth: 220, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {renderedCellValue}
                    </Typography>
                  ) : (
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {renderedCellValue}
                    </Typography>
                  )}
                </Tooltip>
              );
            },
          },
          {
            accessorKey: 'comments',
            header: 'Commentaire',
            size: 260,
            filterVariant: 'text',
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <Typography variant="body2" noWrap title={renderedCellValue} sx={{ color: 'text.secondary', maxWidth: 240 }}>
                  {renderedCellValue}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ),
          },

        ],
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: visibleEntries,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enablePagination: true,
    enableGrouping: true,
    rowCount: total,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    state: { pagination, columnFilters, globalFilter, sorting, grouping, isLoading: loading },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    initialState: {
      density: 'compact',
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'] },
      pagination: { pageIndex: 0, pageSize: 100 },
    },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
    positionToolbarAlertBanner: 'bottom',
    renderDetailPanel: ({ row }) => {
      const detailLines = Array.isArray(row.original.detailLines) ? row.original.detailLines : [];

      if (viewMode !== 'ticket' || detailLines.length === 0) return null;

      return (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Stack spacing={1}>
            {detailLines.map((line) => (
              <Paper key={line.entryId || `${line.user}-${line.spentOn}-${line.hours}`} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" mb={0.75}>
                  <Chip size="small" label={fmtDate(line.spentOn)} variant="outlined" />
                  <Chip size="small" label={line.user || '—'} variant="outlined" />
                  <Chip size="small" label={line.activity || '—'} variant="outlined" />
                  <Chip size="small" label={fmtHours(line.hours)} color="primary" variant="outlined" />
                  {line.tracker ? <Chip size="small" label={line.tracker} variant="outlined" /> : null}
                  {line.facturable ? <Chip size="small" label={line.facturable} variant="outlined" /> : null}
                  {line.deplacement === 'Oui' ? <Chip size="small" icon={<DirectionsCarIcon />} label="Déplacement" /> : null}
                </Stack>
                {line.comments ? (
                  <Typography variant="body2" color="text.secondary">
                    {line.comments}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Aucun commentaire
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      );
    },
    renderToolbarInternalActions: ({ table: currentTable }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, nextValue) => {
            if (nextValue) setViewMode(nextValue);
          }}
        >
          <ToggleButton value="entries">Lignes</ToggleButton>
          <ToggleButton value="ticket">Par ticket</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <FileDownloadIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
          <MenuItem disabled={currentTable.getRowModel().rows.length === 0} onClick={() => handleExportRows(currentTable.getRowModel().rows)}>
            Exporter la page actuelle
          </MenuItem>
          <MenuItem
            disabled={!currentTable.getIsSomeRowsSelected() && !currentTable.getIsAllRowsSelected()}
            onClick={() => handleExportRows(currentTable.getSelectedRowModel().rows)}
          >
            Exporter les lignes sélectionnées
          </MenuItem>
          <MenuItem disabled={entries.length === 0} onClick={handleExportAll}>
            Exporter tout (page courante)
          </MenuItem>
        </Menu>
        <MRT_ToggleDensePaddingButton table={currentTable} />
        <MRT_ToggleFullScreenButton table={currentTable} />
        <MRT_ToggleFiltersButton table={currentTable} />
        <MRT_ToggleGlobalFilterButton table={currentTable} />
      </Box>
    ),
  });

  const trackerData = useMemo(
    () =>
      (summary?.trackers || []).slice(0, 6).map((tracker, index) => ({
        ...tracker,
        color: TRACKER_COLORS[index] || '#94a3b8',
      })),
    [summary]
  );

  const deplacData = useMemo(() => {
    if (!summary) return [];

    return [
      { name: 'Déplacement', value: Number(summary.deplacementHours || 0), color: CHART_COLORS.yes },
      { name: 'Remote', value: Number(summary.nonDeplacementHours || 0), color: CHART_COLORS.no },
    ];
  }, [summary]);

  const deplacPct = summary?.totalHours > 0 ? Math.round((summary.deplacementHours / summary.totalHours) * 100) : 0;
  const commandeTracker = summary?.trackers?.find((item) => item.name?.toLowerCase().includes('commande'));
  const helpdeskTracker = summary?.trackers?.find((item) => item.name?.toLowerCase().includes('helpdesk'));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={1} mb={2}>
        <Typography variant="h6">Temps passé</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccessTimeIcon color="action" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Total page :
          </Typography>
          <Chip label={fmtHours(totalHours)} color="primary" size="small" icon={<AccessTimeIcon />} />
          <Chip label={`${total} entrée${total > 1 ? 's' : ''}`} size="small" variant="outlined" />
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Filtres
        </Typography>
        <Divider orientation="vertical" flexItem />
        <ToggleButtonGroup size="small" value={preset} exclusive onChange={handlePresetChange}>
          {Object.entries(PRESETS).map(([key, { label, icon }]) => (
            <ToggleButton key={key} value={key} sx={{ gap: 0.5, px: 1.5 }}>
              {icon}
              <Typography variant="caption">{label}</Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {preset ? (
          <Chip label={`Filtre actif : ${PRESETS[preset].label}`} onDelete={() => setPreset(null)} size="small" color="primary" />
        ) : null}
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonthIcon fontSize="small" color="action" />
          <TextField
            label="Du"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 145 }}
          />
          <Typography variant="caption" color="text.secondary">
            →
          </Typography>
          <TextField
            label="Au"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 145 }}
          />
          {dateFrom || dateTo ? (
            <Chip label="Effacer dates" size="small" variant="outlined" onDelete={() => { setDateFrom(''); setDateTo(''); }} />
          ) : null}
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Autocomplete
          freeSolo
          options={filterOptions.organizations}
          value={getFilterValue('organization')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('organization', value);
          }}
          onChange={(_, value) => setColumnFilterValue('organization', value || '')}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Organisation" size="small" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.projects}
          value={getFilterValue('project')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('project', value);
          }}
          onChange={(_, value) => setColumnFilterValue('project', value || '')}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Projet ERM" size="small" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.trackers}
          value={getFilterValue('tracker')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('tracker', value);
          }}
          onChange={(_, value) => setColumnFilterValue('tracker', value || '')}
          sx={{ minWidth: 170 }}
          renderInput={(params) => <TextField {...params} label="Tracker" size="small" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.activities}
          value={getFilterValue('activity')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('activity', value);
          }}
          onChange={(_, value) => setColumnFilterValue('activity', value || '')}
          sx={{ minWidth: 180 }}
          renderInput={(params) => <TextField {...params} label="Activité" size="small" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.users}
          value={getFilterValue('user')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('user', value);
          }}
          onChange={(_, value) => setColumnFilterValue('user', value || '')}
          sx={{ minWidth: 180 }}
          renderInput={(params) => <TextField {...params} label="Utilisateur" size="small" />}
        />
        <Autocomplete
          options={filterOptions.deplacement}
          value={getFilterValue('deplacement')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('deplacement', value);
          }}
          onChange={(_, value) => setColumnFilterValue('deplacement', value || '')}
          sx={{ minWidth: 150 }}
          renderInput={(params) => <TextField {...params} label="Déplacement" size="small" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.facturable}
          value={getFilterValue('facturable')}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return;
            setColumnFilterValue('facturable', value);
          }}
          onChange={(_, value) => setColumnFilterValue('facturable', value || '')}
          sx={{ minWidth: 180 }}
          renderInput={(params) => <TextField {...params} label="Facturable" size="small" />}
        />
        <ToggleButtonGroup
          size="small"
          value={grouping[0] || ''}
          exclusive
          onChange={(_, nextValue) => setGrouping(nextValue ? [nextValue] : [])}
        >
          <ToggleButton value="">Aucun groupement</ToggleButton>
          <ToggleButton value="organization">Par organisation</ToggleButton>
          <ToggleButton value="project">Par projet</ToggleButton>
          <ToggleButton value="activity">Par activité</ToggleButton>
          <ToggleButton value="user">Par utilisateur</ToggleButton>
          <ToggleButton value="facturable">Par facturable</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <StatCard
          label="Total heures"
          value={loading ? '…' : fmtHours(summary?.totalHours ?? 0)}
          icon={<AccessTimeIcon fontSize="inherit" />}
          color="#3b82f6"
        />
        <StatCard
          label="Déplacements"
          value={loading ? '…' : fmtHours(summary?.deplacementHours ?? 0)}
          pct={loading ? undefined : deplacPct}
          icon={<DirectionsCarIcon fontSize="inherit" />}
          color="#f59e0b"
        />
        {commandeTracker ? (
          <StatCard
            label="Tracker Commande"
            value={fmtHours(commandeTracker.hours)}
            pct={summary?.totalHours > 0 ? Math.round((commandeTracker.hours / summary.totalHours) * 100) : undefined}
            icon={<ShoppingCartIcon fontSize="inherit" />}
            color="#3b82f6"
          />
        ) : null}
        {helpdeskTracker ? (
          <StatCard
            label="Tracker Helpdesk"
            value={fmtHours(helpdeskTracker.hours)}
            pct={summary?.totalHours > 0 ? Math.round((helpdeskTracker.hours / summary.totalHours) * 100) : undefined}
            icon={<SupportAgentIcon fontSize="inherit" />}
            color="#8b5cf6"
          />
        ) : null}
        <StatCard
          label="Entrées"
          value={loading ? '…' : summary?.totalEntries ?? total}
          icon={<TrendingUpIcon fontSize="inherit" />}
          color="#10b981"
        />
        <Box
          sx={{ flex: 1, minWidth: 140, cursor: overrunCount > 0 || showOverrunsOnly ? 'pointer' : 'default' }}
          onClick={() => {
            if (overrunCount > 0 || showOverrunsOnly) {
              setShowOverrunsOnly((prev) => !prev);
            }
          }}
        >
          <StatCard
            label={viewMode === 'ticket' ? 'Tickets dépassés' : 'Temps dépassés'}
            value={loading ? '…' : overrunCount}
            sub={showOverrunsOnly ? 'Filtre actif' : 'Cliquer pour filtrer'}
            icon={<WarningAmberIcon fontSize="inherit" />}
            color={showOverrunsOnly ? '#f59e0b' : '#b45309'}
          />
        </Box>
      </Stack>

      {showOverrunsOnly ? (
        <Stack direction="row" mb={2}>
          <Chip
            label={`Filtre actif : ${viewMode === 'ticket' ? 'tickets en dépassement' : 'temps en dépassement'}`}
            color="warning"
            onDelete={() => setShowOverrunsOnly(false)}
          />
        </Stack>
      ) : null}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DirectionsCarIcon fontSize="small" sx={{ color: '#f59e0b' }} />
              Déplacements vs. Présentiel
            </Typography>
            {loading ? (
              <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', mt: 1 }} />
            ) : !summary?.totalHours ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Aucune donnée
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={deplacData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {deplacData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(value) => [`${Number(value).toFixed(2)}h`, '']} />
                  <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon fontSize="small" sx={{ color: '#3b82f6' }} />
              Répartition par Tracker
            </Typography>
            {loading ? (
              <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', mt: 1 }} />
            ) : trackerData.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Aucune donnée
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={trackerData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="hours" nameKey="name" labelLine={false} label={renderCustomLabel}>
                    {trackerData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(value) => [`${Number(value).toFixed(2)}h`, '']} />
                  <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 240, overflow: 'auto' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Détail par Tracker
            </Typography>
            {loading ? (
              <Stack spacing={0.5}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton key={item} height={28} />
                ))}
              </Stack>
            ) : trackerData.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Aucune donnée
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {trackerData.map((tracker) => {
                  const pct = summary?.totalHours > 0 ? Math.round((tracker.hours / summary.totalHours) * 100) : 0;

                  return (
                    <Box key={tracker.name}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tracker.color, flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ maxWidth: 130 }}>
                            {tracker.name}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" fontWeight={700}>
                            {fmtHours(tracker.hours)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pct}%
                          </Typography>
                        </Stack>
                      </Stack>
                      <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: tracker.color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" width="100%" height={120} />
          <Skeleton variant="rounded" width="100%" height={260} />
          <Skeleton variant="rectangular" width="100%" height={500} />
        </Stack>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : visibleEntries.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Aucune entrée de temps
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </Box>
  );
}
